import React, { useEffect, useMemo, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import Loader from "../../common/Loader";
import TagInput from "../../common/TagInput";
import useFournisseurs from "./hooks/useFournisseurs";
import useAccessSettings from "../../../hooks/useInit/useAccessSettings";
import { userAccessEnum } from "../../../config/accessEnum";
import styles from "./FournisseurManager.module.scss";

/**
 * Page unifiée de gestion des fournisseurs (master-détail, référentiel
 * simple : nom + description). Réservée aux super admins (LS-658).
 * La sélection est portée par l'URL (`/fournisseurs/:uuid`).
 */
const FournisseurManager = (props) => {
  const {
    fournisseurs,
    isLoading,
    loadFournisseurs,
    creer,
    modifier,
    supprimer,
  } = useFournisseurs();

  const [creating, setCreating] = useState(false);
  const [query, setQuery] = useState("");

  const { accessActions } = useAccessSettings();
  const { granted: canManageFournisseurs } = accessActions.canI({
    action: userAccessEnum.VISUALISER_FOURNISSEUR,
  });

  const routeUuid = props.match.params.uuid || null;
  const selected = useMemo(() => {
    if (creating) return null;
    return (
      fournisseurs.find((f) => f.uuid === routeUuid) ||
      fournisseurs[0] ||
      null
    );
  }, [creating, fournisseurs, routeUuid]);

  const goTo = (uuid) =>
    props.history.push(uuid ? `/fournisseurs/${uuid}` : "/fournisseurs");

  const handleSelect = (uuid) => {
    setCreating(false);
    goTo(uuid);
  };

  const handleCreateStart = () => {
    setCreating(true);
    goTo(null);
  };

  const handleCreate = async (nom, description, emails) => {
    const uuid = await creer(nom, description, emails);
    if (!uuid) return;
    await loadFournisseurs();
    setCreating(false);
    goTo(uuid);
  };

  const handleSave = async (uuid, nom, description, emails) => {
    const ok = await modifier(uuid, nom, description, emails);
    if (ok) await loadFournisseurs();
    return ok;
  };

  const handleDelete = async (uuid) => {
    const ok = await supprimer(uuid);
    if (!ok) return;
    const list = await loadFournisseurs();
    const next = (list || []).find((f) => f.uuid !== uuid);
    goTo(next ? next.uuid : null);
  };

  if (!canManageFournisseurs) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHead}>
          <span className={styles.pageIcon}>
            <i className="fa fa-truck" />
          </span>
          <h1 className={styles.pageTitle}>Fournisseurs</h1>
        </div>
        <div className={styles.empty}>
          <i className="fa fa-lock" />
          <span>Accès réservé aux super admins.</span>
        </div>
      </div>
    );
  }

  if (isLoading && fournisseurs.length === 0) {
    return (
      <div className={styles.page}>
        <div className={styles.loaderWrap}>
          <Loader />
        </div>
      </div>
    );
  }

  const filtered = fournisseurs.filter((f) =>
    (f.nom || "").toLowerCase().includes(query.trim().toLowerCase()),
  );

  return (
    <div className={styles.page}>
      <div className={styles.pageHead}>
        <span className={styles.pageIcon}>
          <i className="fa fa-truck" />
        </span>
        <h1 className={styles.pageTitle}>Fournisseurs</h1>
      </div>

      {fournisseurs.length === 0 && !creating ? (
        <div className={styles.empty}>
          <i className="fa fa-truck" />
          <span>Aucun fournisseur pour le moment.</span>
          <button
            type="button"
            className={styles.emptyBtn}
            onClick={handleCreateStart}
          >
            <i className="fa fa-plus" /> Créer un fournisseur
          </button>
        </div>
      ) : (
        <div className={styles.layout}>
          <div className={styles.rail}>
            <div className={styles.toolbar}>
              <button
                type="button"
                className={styles.addBtn}
                onClick={handleCreateStart}
              >
                <i className="fa fa-plus" /> Nouveau fournisseur
              </button>
              <div className={styles.search}>
                <i className="fa fa-search" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher un fournisseur…"
                  aria-label="Rechercher un fournisseur"
                />
              </div>
            </div>
            <div className={styles.list} role="listbox" aria-label="Fournisseurs">
              {filtered.length === 0 ? (
                <div className={styles.noResult}>Aucun fournisseur trouvé.</div>
              ) : (
                filtered.map((f) => {
                  const isActive = f.uuid === (creating ? null : selected?.uuid);
                  return (
                    <button
                      type="button"
                      key={f.uuid}
                      role="option"
                      aria-selected={isActive}
                      className={`${styles.item} ${isActive ? styles.itemActive : ""}`}
                      onClick={() => handleSelect(f.uuid)}
                    >
                      <span className={styles.itemName}>{f.nom}</span>
                    </button>
                  );
                })
              )}
            </div>
            <div className={styles.count}>
              {fournisseurs.length} fournisseur
              {fournisseurs.length > 1 ? "s" : ""}
            </div>
          </div>

          <FournisseurForm
            fournisseur={selected}
            creating={creating}
            onCreate={handleCreate}
            onSave={handleSave}
            onDelete={handleDelete}
          />
        </div>
      )}
    </div>
  );
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FournisseurForm = ({
  fournisseur,
  creating,
  onCreate,
  onSave,
  onDelete,
}) => {
  const [nom, setNom] = useState("");
  const [description, setDescription] = useState("");
  // LS-687 — carnet d'adresses (remplace l'email unique, LS-667) : jusqu'à ~20
  // interlocuteurs, proposés en autosuggestion à l'envoi de l'accord (LS-668).
  const [emails, setEmails] = useState([]);
  const [busy, setBusy] = useState(false);
  const nomInputRef = useRef(null);

  useEffect(() => {
    setNom(creating ? "" : fournisseur?.nom || "");
    setDescription(creating ? "" : fournisseur?.description || "");
    setEmails(creating ? [] : fournisseur?.emails || []);
  }, [creating, fournisseur]);

  useEffect(() => {
    if (creating) nomInputRef.current?.focus();
  }, [creating]);

  if (!creating && !fournisseur) {
    return (
      <div className={styles.panel}>
        <div className={styles.invite}>
          <i className="fa fa-hand-pointer" />
          <span>Sélectionnez un fournisseur pour le modifier.</span>
        </div>
      </div>
    );
  }

  const emailInvalide = emails.some((e) => !EMAIL_REGEX.test(e));

  const hasChanges =
    !creating &&
    (nom.trim() !== (fournisseur?.nom || "") ||
      description !== (fournisseur?.description || "") ||
      JSON.stringify(emails) !== JSON.stringify(fournisseur?.emails || []));

  const submit = async () => {
    const trimmedNom = nom.trim();
    if (!trimmedNom || busy || emailInvalide) return;
    setBusy(true);
    if (creating) {
      await onCreate(trimmedNom, description || null, emails);
    } else {
      await onSave(fournisseur.uuid, trimmedNom, description || null, emails);
    }
    setBusy(false);
  };

  const handleDelete = () => {
    if (
      !window.confirm(
        `Supprimer le fournisseur « ${fournisseur.nom} » ? Cette action est définitive.`,
      )
    ) {
      return;
    }
    onDelete(fournisseur.uuid);
  };

  return (
    <div className={styles.panel}>
      <div className={styles.formRow}>
        <label className={styles.formLabel} htmlFor="fournisseur-nom">
          Nom
        </label>
        <input
          id="fournisseur-nom"
          ref={nomInputRef}
          className={styles.formInput}
          value={nom}
          onChange={(e) => setNom(e.target.value)}
          placeholder="ex. AUTOMOBILES RÉUNION"
        />
      </div>

      <div className={styles.formRow}>
        <label className={styles.formLabel} htmlFor="fournisseur-description">
          Description
        </label>
        <textarea
          id="fournisseur-description"
          className={styles.formTextarea}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Adresse, SIRET…"
          rows={6}
        />
      </div>

      <div className={styles.formRow}>
        <TagInput
          name="emails"
          label="Carnet d'adresses"
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
          placeholder="Ajouter un email..."
        />
        {emailInvalide && (
          <p className={styles.formError}>
            Une ou plusieurs adresses email ne sont pas valides.
          </p>
        )}
      </div>

      <div className={styles.formActions}>
        <button
          type="button"
          className={styles.btnSave}
          onClick={submit}
          disabled={
            !nom.trim() || busy || emailInvalide || (!creating && !hasChanges)
          }
        >
          <i className="fa fa-check" /> {creating ? "Créer" : "Enregistrer"}
        </button>
        {!creating && (
          <button
            type="button"
            className={styles.btnDelete}
            onClick={handleDelete}
            disabled={busy}
          >
            <i className="fa fa-trash" /> Supprimer
          </button>
        )}
      </div>
    </div>
  );
};

export default withRouter(FournisseurManager);
